export interface DotField {
  init(): void
  destroy(): void
}

export interface DotFieldOptions {
  dotRadius?: number
  dotSpacing?: number
  cursorRadius?: number
  bulgeStrength?: number
  glowRadius?: number
  gradientFrom?: string
  gradientTo?: string
  alwaysBulge?: boolean
}

interface Dot { ax: number; ay: number; sx: number; sy: number }

export function createDotField(canvasEl: HTMLCanvasElement, options: DotFieldOptions = {}): DotField {
  if (typeof window === 'undefined' || !canvasEl.parentElement) return { init: () => undefined, destroy: () => undefined }

  const CONFIG: Required<DotFieldOptions> = Object.assign({
    dotRadius: 1.2, dotSpacing: 18, cursorRadius: 640, bulgeStrength: 101, glowRadius: 150,
    gradientFrom: 'rgba(107, 61, 255, 0.16)', gradientTo: 'rgba(168, 130, 240, 0.20)', alwaysBulge: false
  }, options)
  const maybeCtx = canvasEl.getContext('2d', { alpha: true })
  if (!maybeCtx) return { init: () => undefined, destroy: () => undefined }
  const ctx: CanvasRenderingContext2D = maybeCtx
  const dpr = Math.min(window.devicePixelRatio || 1, 2)
  const TWO_PI = Math.PI * 2
  let dots: Dot[] = []
  const mouse = { x: -9999, y: -9999, prevX: -9999, prevY: -9999, speed: 0 }
  let smoothIntensity = 0
  let frameId: number | null = null
  let layoutW = 0
  let layoutH = 0
  let running = false
  let resizeTimer: ReturnType<typeof setTimeout> | null = null
  let svgEl: SVGSVGElement | null = null
  let glowCircle: SVGCircleElement | null = null
  let speedTimer: ReturnType<typeof setInterval> | null = null
  let mouseInCanvas = false

  function createGlowSVG(): void {
    const parent = canvasEl.parentElement!
    svgEl = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
    svgEl.setAttribute('style', 'position:absolute;inset:0;width:100%;height:100%;pointer-events:none;z-index:0;')
    const defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs')
    const radGrad = document.createElementNS('http://www.w3.org/2000/svg', 'radialGradient')
    radGrad.setAttribute('id', `dot-field-glow-${Date.now()}`)
    const stop1 = document.createElementNS('http://www.w3.org/2000/svg', 'stop')
    stop1.setAttribute('offset', '0%'); stop1.setAttribute('stop-color', '#ffffff')
    const stop2 = document.createElementNS('http://www.w3.org/2000/svg', 'stop')
    stop2.setAttribute('offset', '100%'); stop2.setAttribute('stop-color', '#ffffff00')
    radGrad.append(stop1, stop2); defs.appendChild(radGrad); svgEl.appendChild(defs)
    glowCircle = document.createElementNS('http://www.w3.org/2000/svg', 'circle')
    glowCircle.setAttribute('cx', '-9999'); glowCircle.setAttribute('cy', '-9999')
    glowCircle.setAttribute('r', String(CONFIG.glowRadius))
    glowCircle.setAttribute('fill', `url(#${radGrad.getAttribute('id')})`)
    glowCircle.setAttribute('style', 'opacity:0;will-change:opacity;')
    svgEl.appendChild(glowCircle)
    parent.appendChild(svgEl)
  }

  function layout(): void {
    const rect = canvasEl.parentElement!.getBoundingClientRect()
    const w = rect.width; const h = rect.height
    layoutW = w; layoutH = h
    canvasEl.width = w * dpr; canvasEl.height = h * dpr
    canvasEl.style.width = `${w}px`; canvasEl.style.height = `${h}px`
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    const cell = CONFIG.dotRadius + CONFIG.dotSpacing
    const cols = Math.floor(w / cell); const rows = Math.floor(h / cell)
    const padX = (w % cell) / 2; const padY = (h % cell) / 2
    dots = new Array(rows * cols)
    let idx = 0
    for (let r = 0; r < rows; r++) for (let c = 0; c < cols; c++) {
      const ax = padX + c * cell + cell / 2
      const ay = padY + r * cell + cell / 2
      dots[idx++] = { ax, ay, sx: ax, sy: ay }
    }
  }

  function getCanvasRect(): DOMRect | Pick<DOMRect, 'left' | 'top' | 'right' | 'bottom'> {
    return canvasEl.parentElement?.getBoundingClientRect() ?? { left: 0, top: 0, right: 0, bottom: 0 }
  }
  function onMouseMove(event: MouseEvent): void {
    const rect = getCanvasRect()
    mouse.x = event.clientX - rect.left; mouse.y = event.clientY - rect.top
    if (CONFIG.alwaysBulge) mouseInCanvas = event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom
  }
  function updateSpeed(): void {
    const dx = mouse.prevX - mouse.x; const dy = mouse.prevY - mouse.y
    mouse.speed += (Math.sqrt(dx * dx + dy * dy) - mouse.speed) * 0.5
    if (mouse.speed < 0.001) mouse.speed = 0
    mouse.prevX = mouse.x; mouse.prevY = mouse.y
  }
  function render(): void {
    if (!running) return
    const R = CONFIG.cursorRadius; const R2 = R * R; const dotR = CONFIG.dotRadius
    const targetIntensity = CONFIG.alwaysBulge ? (mouseInCanvas ? 1 : 0) : Math.min(mouse.speed / 5, 1)
    smoothIntensity += (targetIntensity - smoothIntensity) * 0.06
    if (smoothIntensity < 0.001) smoothIntensity = 0
    if (glowCircle) {
      glowCircle.setAttribute('cx', String(mouse.x)); glowCircle.setAttribute('cy', String(mouse.y))
      glowCircle.style.opacity = String(smoothIntensity * 0.6)
    }
    ctx.clearRect(0, 0, layoutW, layoutH)
    const grad = ctx.createLinearGradient(0, 0, layoutW, layoutH)
    grad.addColorStop(0, CONFIG.gradientFrom); grad.addColorStop(1, CONFIG.gradientTo); ctx.fillStyle = grad
    ctx.beginPath()
    for (const dot of dots) {
      const dx = mouse.x - dot.ax; const dy = mouse.y - dot.ay; const d2 = dx * dx + dy * dy
      if (d2 < R2 && smoothIntensity > 0.01) {
        const factor = 1 - Math.sqrt(d2) / R
        const bulge = factor * factor * CONFIG.bulgeStrength * smoothIntensity
        const angle = Math.atan2(dy, dx)
        dot.sx += (dot.ax - Math.cos(angle) * bulge - dot.sx) * 0.15
        dot.sy += (dot.ay - Math.sin(angle) * bulge - dot.sy) * 0.15
      } else {
        dot.sx += (dot.ax - dot.sx) * 0.1; dot.sy += (dot.ay - dot.sy) * 0.1
      }
      ctx.moveTo(dot.sx + dotR, dot.sy); ctx.arc(dot.sx, dot.sy, dotR, 0, TWO_PI)
    }
    ctx.fill()
    frameId = requestAnimationFrame(render)
  }
  function onResize(): void {
    if (resizeTimer) clearTimeout(resizeTimer)
    resizeTimer = setTimeout(layout, 100)
  }
  function onMouseLeave(): void { mouseInCanvas = false }

  return {
    init(): void {
      if (running) return
      running = true; createGlowSVG(); layout()
      window.addEventListener('mousemove', onMouseMove, { passive: true })
      window.addEventListener('resize', onResize, { passive: true })
      if (CONFIG.alwaysBulge) canvasEl.parentElement?.addEventListener('mouseleave', onMouseLeave)
      speedTimer = setInterval(updateSpeed, 20)
      frameId = requestAnimationFrame(render)
    },
    destroy(): void {
      running = false
      if (frameId !== null) { cancelAnimationFrame(frameId); frameId = null }
      if (speedTimer) { clearInterval(speedTimer); speedTimer = null }
      if (resizeTimer) { clearTimeout(resizeTimer); resizeTimer = null }
      window.removeEventListener('mousemove', onMouseMove)
      window.removeEventListener('resize', onResize)
      if (CONFIG.alwaysBulge) canvasEl.parentElement?.removeEventListener('mouseleave', onMouseLeave)
      svgEl?.remove(); svgEl = null; glowCircle = null; dots = []
      ctx.clearRect(0, 0, canvasEl.width, canvasEl.height)
    }
  }
}
